/**
 * AdminPage — main admin panel.
 *
 * Action buttons (admin only): Stock Management · AI PDF Import · Storage.
 * Storage is a dialog (admin-only) showing the storage stats card on demand.
 * Tabs: Products / Bulk Upload / Staff (Staff is admin-only).
 */
import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from '../components/ui/dropdown-menu';
import { useAuth, api } from '../lib/store';
import { Sparkles, Package, Database, FileText, MessageCircle, MoreVertical, Settings } from 'lucide-react';
import { toast } from 'sonner';

import { ProductManagement } from '../components/admin/ProductManagement';
import { BulkUpload } from '../components/admin/BulkUpload';
import { UserManagement } from '../components/admin/UserManagement';
import StorageStatsCard from '../components/admin/StorageStatsCard';
import SettingsManagement from '../components/admin/SettingsManagement';
import AnalyticsDashboard from '../components/admin/AnalyticsDashboard';
import CatalogHealthPage from './CatalogHealthPage';
import WhatsAppAdminPanel from '../components/admin/WhatsAppAdminPanel';
import WalkinRegisterPanel from '../components/admin/WalkinRegisterPanel';
import LeadRegisterPanel from '../components/admin/LeadRegisterPanel';
import ComplaintsPanel from '../components/admin/ComplaintsPanel';
import TodayDashboardPanel from '../components/admin/TodayDashboardPanel';
import ClientErrorsPanel from '../components/admin/ClientErrorsPanel';
import ReportsPanel from '../components/admin/ReportsPanel';
import RolePermissionsPanel from '../components/admin/RolePermissionsPanel';

// Tab -> the permission that shows it (used to pick the landing tab).
const TAB_PERMISSION = {
  staff: 'users.view_team', walkins: 'walkins.view_all', leads: 'leads.view_all',
  complaints: 'complaints.view_all', analytics: 'reports.company',
  'catalog-health': 'catalog.health', whatsapp: 'whatsapp.admin', errors: 'system.errors', settings: 'settings.edit',
};

export default function AdminPage() {
  const { isAdmin, can, canOpenAdmin } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [showStorage, setShowStorage] = useState(false);

  // URL-driven tab selection. Lets the QuotationDetail "Back"
  // button return to Admin → Staff tab with the correct staff card
  // pre-expanded (see UserManagement's expandUser useEffect). Falls back
  // to the role-appropriate default when no tab param is present.
  const defaultTab = can('reports.today') ? 'today'
    : can('products.manage') ? 'products'
    : ['staff', 'walkins', 'leads', 'complaints', 'analytics', 'catalog-health', 'whatsapp', 'errors', 'settings']
      .find((t) => can(TAB_PERMISSION[t])) || 'products';
  const urlTab = searchParams.get('tab');
  const activeTab = urlTab || defaultTab;
  const handleTabChange = (v) => {
    // Preserve any other query params (expandUser, etc.) — only replace
    // `tab`. Otherwise clicking a tab would drop the expandUser hint.
    const next = new URLSearchParams(searchParams);
    if (v === defaultTab && !next.has('expandUser')) {
      next.delete('tab');
    } else {
      next.set('tab', v);
    }
    setSearchParams(next, { replace: true });
  };

  // Download Roles & Permissions guide PDF — sharable with the staff team.
  const handleDownloadRolesGuide = async () => {
    try {
      const res = await api.downloadRolesGuide();
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'HEDA_Plywoods_Roles_and_Permissions_Guide.pdf';
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Roles guide downloaded');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to download roles guide');
    }
  };

  // Download Pending Quotations report PDF — admin only, on-demand.
  const handleDownloadPendingReport = async () => {
    try {
      const res = await api.downloadPendingReport();
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      const today = new Date().toISOString().slice(0, 10);
      a.href = url;
      a.download = `HEDA_Pending_Quotations_${today}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Pending report downloaded');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to download pending report');
    }
  };

  if (!canOpenAdmin) {
    return (
      <div className="text-center py-12">
        <p className="text-heda-mute">Product management access required</p>
      </div>
    );
  }

  // ─── Flat pill nav config ──────────────────────────────────────────
  // The panel exposes these top-level pills in this exact order:
  //   Analytics | Reports | Staff | Today | Walk-ins | Leads | Complaints | Catalog | System
  // Most open a single tab directly. Catalog and System are groups —
  // clicking them reveals a second-row tab strip.
  //
  // Tab VALUES, data-testids, and role conditionals stay identical to
  // the previous grouped layout so `?tab=…` deep links keep working.
  const PILLS = [
    {
      key: 'analytics', label: 'Analytics',
      tabs: [{ value: 'analytics', canShow: can('reports.company') }],
    },
    {
      key: 'reports', label: 'Reports',
      tabs: [{ value: 'reports', canShow: can('reports.company') }],
    },
    {
      key: 'staff', label: 'Staff',
      tabs: [{ value: 'staff', canShow: can('users.view_team') }],
    },
    {
      key: 'today', label: 'Today',
      tabs: [{ value: 'today', canShow: can('reports.today') }],
    },
    {
      key: 'walkins', label: 'Walk-ins',
      tabs: [{ value: 'walkins', canShow: can('walkins.view_all') }],
    },
    {
      key: 'leads', label: 'Leads',
      tabs: [{ value: 'leads', canShow: can('leads.view_all') }],
    },
    {
      key: 'complaints', label: 'Complaints',
      tabs: [{ value: 'complaints', canShow: can('complaints.view_all') }],
    },
    {
      key: 'catalog', label: 'Catalog', isGroup: true,
      tabs: [
        { value: 'products', canShow: can('products.manage') },
        { value: 'bulk', canShow: can('products.manage') },
        { value: 'catalog-health', canShow: can('catalog.health') },
      ],
    },
    {
      key: 'system', label: 'System', isGroup: true,
      tabs: [
        { value: 'whatsapp', canShow: can('whatsapp.admin') },
        { value: 'errors', canShow: can('system.errors') },
        { value: 'settings', canShow: can('settings.edit') },
        { value: 'roles', canShow: isAdmin },
      ],
    },
  ];

  // Reverse map — active pill derived from the currently-active tab so
  // deep links (?tab=settings) automatically land on the right pill.
  const TAB_TO_PILL = PILLS.reduce((acc, p) => {
    p.tabs.forEach((t) => { acc[t.value] = p.key; });
    return acc;
  }, {});
  const activePill = TAB_TO_PILL[activeTab] || 'catalog';

  // Pills with zero visible tabs for this role are hidden entirely so
  // e.g. a manager doesn't see an empty "System" pill.
  const visiblePills = PILLS.filter((p) => p.tabs.some((t) => t.canShow));

  const handlePillClick = (pillKey) => {
    if (pillKey === activePill) return;
    const p = PILLS.find((x) => x.key === pillKey);
    const first = p && p.tabs.find((t) => t.canShow);
    if (first) handleTabChange(first.value);
  };

  const activePillConfig = PILLS.find((p) => p.key === activePill);
  const showSubRow = !!activePillConfig?.isGroup;

  // Level-2 (sub-tab row for Catalog/System groups) styling.
  const L2_CLS =
    'rounded-full px-3 py-1 text-[11px] font-semibold uppercase tracking-wider text-heda-mute whitespace-nowrap data-[state=active]:bg-heda-ink data-[state=active]:text-white data-[state=active]:shadow-none';

  return (
    <div className="space-y-6" data-testid="admin-page">
      {/* Header — title + actions. Stacks on phones so the action pills
          wrap under the title. */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <h1 className="flex items-center gap-2 font-display text-[20px] font-bold text-heda-ink">
          <Settings size={18} className="text-heda-gold" /> Admin Panel
        </h1>
        <div className="flex flex-wrap gap-2">
          {can('stock.manage') && (
            <button type="button" onClick={() => navigate('/stock')} className="heda-pill" data-testid="stock-management-btn">
              <Package size={13} /> Stock
            </button>
          )}
          {can('purchase_orders.manage') && (
            <button type="button" onClick={() => navigate('/purchase-orders')} className="heda-pill" data-testid="purchase-orders-btn">
              <Package size={13} /> Purchase Orders
            </button>
          )}
          {can('products.manage') && (
            <button type="button" onClick={() => navigate('/ai-import')} className="heda-pill" data-testid="ai-pdf-import-btn">
              <Sparkles size={13} /> AI PDF Import
            </button>
          )}
          {(isAdmin || can('system.backup') || can('reports.company')) && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button type="button" className="heda-pill" data-testid="admin-more-btn" aria-label="More admin actions">
                  <MoreVertical size={13} />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-52 rounded-xl" data-testid="admin-more-menu">
                {can('system.backup') && (
                <DropdownMenuItem onClick={() => setShowStorage(true)} data-testid="storage-btn">
                  <Database className="w-4 h-4 mr-2" /> Storage
                </DropdownMenuItem>
                )}
                {can('reports.company') && (
                <DropdownMenuItem
                  onClick={handleDownloadPendingReport}
                  data-testid="download-pending-report-btn"
                  title="Pending quotations by salesperson, no activity for 3+ days"
                >
                  <FileText className="w-4 h-4 mr-2" /> Pending Report
                </DropdownMenuItem>
                )}
                {isAdmin && (
                <DropdownMenuItem
                  onClick={handleDownloadRolesGuide}
                  data-testid="download-roles-guide-btn"
                  title="Download a PDF guide listing every role and its features — share with your staff team"
                >
                  <FileText className="w-4 h-4 mr-2" /> Roles Guide
                </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </div>

      {/* Storage modal — admin only */}
      <Dialog open={showStorage} onOpenChange={setShowStorage}>
        <DialogContent className="border-heda-line bg-white sm:rounded-3xl max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Database className="w-5 h-5 text-heda-gold" /> Storage Usage
            </DialogTitle>
          </DialogHeader>
          {showStorage && <StorageStatsCard />}
        </DialogContent>
      </Dialog>

      <Tabs value={activeTab} onValueChange={handleTabChange} className="w-full">
        {/* ── Level 1: the section tabs (.heda-tab row). Scrolls sideways
            on narrow screens so nothing gets clipped. ─────────────── */}
        <div
          className="-mx-4 mb-4 flex gap-1 overflow-x-auto border-b border-heda-line px-4 sm:mx-0 sm:px-0"
          data-testid="admin-pill-nav"
        >
          {visiblePills.map((p) => {
            const isActive = activePill === p.key;
            return (
              <button
                key={p.key}
                type="button"
                onClick={() => handlePillClick(p.key)}
                className={`heda-tab whitespace-nowrap ${isActive ? 'is-on' : ''}`}
                data-testid={`admin-pill-${p.key}`}
                aria-pressed={isActive}
              >
                {p.label}
              </button>
            );
          })}
        </div>

        {/* ── Level 2: sub-tab strip ONLY for group pills (Catalog,
            System). Direct pills (Analytics, Reports, Staff, Today,
            Walk-ins, Leads, Complaints) skip this row entirely. ─────────── */}
        {showSubRow && (
          <div className="mb-4 overflow-x-auto">
            <TabsList className="inline-flex h-auto w-max gap-1 rounded-full border border-heda-line bg-white p-1">
              {activePill === 'catalog' && (
                <>
                  {can('products.manage') && (
                  <>
                  <TabsTrigger value="products" className={L2_CLS}>
                    Products
                  </TabsTrigger>
                  <TabsTrigger value="bulk" className={L2_CLS}>
                    Bulk Upload
                  </TabsTrigger>
                  </>
                  )}
                  {can('catalog.health') && (
                    <TabsTrigger value="catalog-health" className={L2_CLS} data-testid="admin-tab-catalog-health">
                      Catalog Health
                    </TabsTrigger>
                  )}
                </>
              )}
              {activePill === 'system' && (
                <>
                  {can('whatsapp.admin') && (
                    <TabsTrigger value="whatsapp" className={L2_CLS} data-testid="admin-tab-whatsapp">
                      <MessageCircle className="w-3.5 h-3.5 mr-1.5" /> WhatsApp
                      <span className="ml-1 rounded-full bg-amber-50 px-1.5 py-0.5 text-[9px] font-bold text-amber-700">BETA</span>
                    </TabsTrigger>
                  )}
                  {can('system.errors') && (
                    <TabsTrigger value="errors" className={L2_CLS} data-testid="admin-tab-errors">
                      Errors
                    </TabsTrigger>
                  )}
                  {can('settings.edit') && (
                    <TabsTrigger value="settings" className={L2_CLS} data-testid="admin-tab-settings">
                      PDF / T&amp;C
                    </TabsTrigger>
                  )}
                  {isAdmin && (
                    <TabsTrigger value="roles" className={L2_CLS} data-testid="admin-tab-roles">
                      Roles
                    </TabsTrigger>
                  )}
                </>
              )}
            </TabsList>
          </div>
        )}

        {can('products.manage') && (
          <TabsContent value="products"><ProductManagement /></TabsContent>
        )}
        {can('reports.today') && (
          <TabsContent value="today"><TodayDashboardPanel /></TabsContent>
        )}
        {can('products.manage') && (
          <TabsContent value="bulk"><BulkUpload /></TabsContent>
        )}
        {can('users.view_team') && (
          <TabsContent value="staff"><UserManagement /></TabsContent>
        )}
        {can('reports.company') && (
          <TabsContent value="analytics"><AnalyticsDashboard /></TabsContent>
        )}
        {can('reports.company') && (
          <TabsContent value="reports"><ReportsPanel /></TabsContent>
        )}
        {can('whatsapp.admin') && (
          <TabsContent value="whatsapp"><WhatsAppAdminPanel /></TabsContent>
        )}
        {can('catalog.health') && (
          <TabsContent value="catalog-health"><CatalogHealthPage /></TabsContent>
        )}
        {can('walkins.view_all') && (
          <TabsContent value="walkins"><WalkinRegisterPanel /></TabsContent>
        )}
        {can('leads.view_all') && (
          <TabsContent value="leads"><LeadRegisterPanel /></TabsContent>
        )}
        {can('complaints.view_all') && (
          <TabsContent value="complaints"><ComplaintsPanel /></TabsContent>
        )}
        {can('system.errors') && (
          <TabsContent value="errors"><ClientErrorsPanel /></TabsContent>
        )}
        {can('settings.edit') && (
          <TabsContent value="settings"><SettingsManagement /></TabsContent>
        )}
        {isAdmin && (
          <TabsContent value="roles"><RolePermissionsPanel /></TabsContent>
        )}
      </Tabs>
    </div>
  );
}
